import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { z } from 'zod';
import { T, SHADOW, RADIUS } from '@/lib/tokens';
import { useBreakpoint } from '@/hooks/useMobile';
import shareShackIcon from '@/assets/logo-transparent.svg';
import logoOnWhite from '@/assets/logo-on-white.svg';

import {
  PENDING_INVITE_STORAGE_KEY,
  INVITE_CODE_REGEX,
  INVITE_INTENT_TTL_MS,
  getAppUrl,
  type PendingInviteIntent,
} from '@/lib/constants';

const emailSchema = z.string().email('Please enter a valid email address');
const passwordSchema = z.string().min(6, 'Password must be at least 6 characters');

type AuthLocationState = {
  inviteIntent?: PendingInviteIntent;
  from?: string;
};

const pendingInviteIntentSchema = z.object({
  code: z.string().regex(INVITE_CODE_REGEX),
  createdAt: z.number().int().positive(),
  itemId: z.string().uuid().optional(),
});

const parsePendingInviteIntent = (rawValue: string | null): PendingInviteIntent | null => {
  if (!rawValue) return null;
  try {
    const parsed = JSON.parse(rawValue);
    const result = pendingInviteIntentSchema.safeParse(parsed);
    return result.success ? (result.data as PendingInviteIntent) : null;
  } catch {
    return null;
  }
};

const isInviteIntentExpired = (intent: PendingInviteIntent) => {
  const age = Date.now() - intent.createdAt;
  return age < 0 || age > INVITE_INTENT_TTL_MS;
};

/**
 * Styled form input matching the design token palette
 * @param root0
 * @param root0.id
 * @param root0.type
 * @param root0.placeholder
 * @param root0.value
 * @param root0.onChange
 * @param root0.error
 */
function AuthInput({
  id,
  type,
  placeholder,
  value,
  onChange,
  error,
}: {
  id: string;
  type: string;
  placeholder: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string;
}) {
  const [focused, setFocused] = useState(false);
  return (
    <div>
      <input
        id={id}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          width: '100%',
          padding: '11px 14px',
          border: `1.5px solid ${error ? T.danger : focused ? T.accent : T.border}`,
          borderRadius: RADIUS.md,
          background: T.surfaceSubtle,
          color: T.text,
          fontSize: 14,
          fontFamily: 'inherit',
          outline: 'none',
          boxSizing: 'border-box',
          transition: 'border-color 0.15s',
        }}
      />
      {error && <p style={{ margin: '4px 0 0', fontSize: 12, color: T.danger }}>{error}</p>}
    </div>
  );
}

/**
 * Feature card for the marketing left column
 * @param root0
 * @param root0.icon
 * @param root0.title
 * @param root0.body
 */
function FeatureCard({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: RADIUS.lg,
        padding: 16,
        boxShadow: hovered ? SHADOW.lg : SHADOW.md,
        transform: hovered ? 'translateY(-2px)' : 'translateY(0)',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: RADIUS.sm,
          background: T.surfaceSubtle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 16,
          marginBottom: 10,
        }}
      >
        {icon}
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 12, color: T.textMuted, lineHeight: 1.55 }}>{body}</div>
    </div>
  );
}

/**
 *
 */
export default function Auth() {
  const [mode, setMode] = useState<'login' | 'signup' | 'forgot'>('login');
  const isLogin = mode === 'login';
  const isForgot = mode === 'forgot';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [inviteNotice, setInviteNotice] = useState<string | null>(null);
  const [signupSuccess, setSignupSuccess] = useState(false);

  const { signIn, signUp, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const bp = useBreakpoint();
  const isWide = bp === 'tablet' || bp === 'desktop';

  // Single effect: persist invite intent from router state, then act on it
  // when authenticated. Consolidating prevents a race where Effect 2 runs
  // before Effect 1 writes to sessionStorage.
  useEffect(() => {
    // Step 1: Persist invite intent from router state (if present)
    const state = location.state as AuthLocationState | null;
    const intentFromState = state?.inviteIntent;
    if (intentFromState) {
      const result = pendingInviteIntentSchema.safeParse(intentFromState);
      if (!result.success || isInviteIntentExpired(result.data as PendingInviteIntent)) {
        sessionStorage.removeItem(PENDING_INVITE_STORAGE_KEY);
        setInviteNotice(
          'Your invite link has expired or is invalid. Please open the invite link again.',
        );
        return;
      }
      // [LEARN] sessionStorage (not localStorage) is used here because it clears when the tab closes,
      // making it safer for temporary data like invite intents that shouldn't persist indefinitely.
      sessionStorage.setItem(PENDING_INVITE_STORAGE_KEY, JSON.stringify(result.data));
    }

    // Step 2: Act on stored invite intent once authenticated
    if (!user) return;

    const storedIntent = parsePendingInviteIntent(
      sessionStorage.getItem(PENDING_INVITE_STORAGE_KEY),
    );
    if (!storedIntent) {
      sessionStorage.removeItem(PENDING_INVITE_STORAGE_KEY);
      // If ProtectedRoute passed a `from` path (e.g. deep link before auth),
      // redirect back there instead of the default dashboard.
      const fromPath = (location.state as AuthLocationState | null)?.from;
      navigate(fromPath && fromPath !== '/auth' ? fromPath : '/dashboard');
      return;
    }

    if (isInviteIntentExpired(storedIntent)) {
      sessionStorage.removeItem(PENDING_INVITE_STORAGE_KEY);
      toast.error('Invite expired', {
        description: 'That invite is too old. Please open a fresh invite link and sign in again.',
      });
      navigate('/dashboard');
      return;
    }

    if (!INVITE_CODE_REGEX.test(storedIntent.code)) {
      sessionStorage.removeItem(PENDING_INVITE_STORAGE_KEY);
      toast.error('Invalid invite', {
        description: 'We could not read that invite code. Please open the invite link again.',
      });
      navigate('/dashboard');
      return;
    }

    sessionStorage.removeItem(PENDING_INVITE_STORAGE_KEY);
    navigate(
      `/invite/${storedIntent.code}${storedIntent.itemId ? `?item=${storedIntent.itemId}` : ''}`,
    );
  }, [user, navigate, location.state]);

  const validateForm = () => {
    const newErrors: { email?: string; password?: string } = {};
    const emailResult = emailSchema.safeParse(email);
    if (!emailResult.success) newErrors.email = emailResult.error.errors[0].message;
    const passwordResult = passwordSchema.safeParse(password);
    if (!passwordResult.success) newErrors.password = passwordResult.error.errors[0].message;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isForgot) {
      const emailResult = emailSchema.safeParse(email);
      if (!emailResult.success) {
        setErrors({ email: emailResult.error.errors[0].message });
        return;
      }
      setIsLoading(true);
      try {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${getAppUrl()}/reset-password`,
        });
        if (error) {
          toast.error('Error', { description: error.message });
        } else {
          toast.success('Check your email', { description: 'We sent you a password reset link.' });
          setMode('login');
        }
      } finally {
        setIsLoading(false);
      }
      return;
    }

    if (!validateForm()) return;
    setIsLoading(true);

    try {
      if (isLogin) {
        const { error } = await signIn(email, password);
        if (error) {
          toast.error('Login failed', {
            description:
              error.message === 'Invalid login credentials'
                ? 'Invalid email or password. Please try again.'
                : error.message,
          });
        }
      } else {
        const { error } = await signUp(email, password, displayName);
        if (error) {
          let errorMessage = error.message;
          if (error.message.includes('already registered')) {
            errorMessage =
              "If this email is registered, you'll receive a confirmation. Otherwise, please try signing in.";
          } else if (
            error.message.toLowerCase().includes('weak') ||
            error.message.toLowerCase().includes('breach') ||
            error.message.toLowerCase().includes('pwned')
          ) {
            errorMessage =
              "This password has appeared in a known data breach and cannot be used. Please choose a unique password that you haven't used on other sites.";
          }
          toast.error('Sign up failed', { description: errorMessage });
        } else {
          setSignupSuccess(true);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ── Signup success screen ─────────────────────────────────────────────────
  if (signupSuccess) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: T.bg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 20,
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 420,
            background: T.surface,
            borderRadius: RADIUS.xl,
            padding: '48px 28px',
            textAlign: 'center',
            border: `1px solid ${T.border}`,
            boxShadow: SHADOW.lg,
          }}
        >
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: '50%',
              background: T.successSoft,
              border: `1px solid ${T.successBorder}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              fontSize: 24,
            }}
          >
            ✉️
          </div>
          <h2
            style={{
              margin: '0 0 10px',
              fontSize: 22,
              fontWeight: 600,
              color: T.text,
              letterSpacing: '-0.02em',
            }}
          >
            Check your email
          </h2>
          <p style={{ margin: '0 0 28px', fontSize: 14, color: T.textSec, lineHeight: 1.65 }}>
            We've sent a verification link to <strong>{email}</strong>. Click the link to activate
            your account, then come back and sign in.
          </p>
          <button
            onClick={() => {
              setSignupSuccess(false);
              setMode('login');
            }}
            style={{
              width: '100%',
              padding: '13px',
              borderRadius: RADIUS.md,
              background: T.surfaceSubtle,
              color: T.textSec,
              border: `1px solid ${T.border}`,
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  // ── Auth form card (shared between layouts) ───────────────────────────────
  const authCard = (
    <div
      style={{
        width: '100%',
        maxWidth: 420,
        background: T.surface,
        borderRadius: RADIUS.xl,
        overflow: 'hidden',
        border: `1px solid ${T.border}`,
        boxShadow: SHADOW.lg,
      }}
    >
      {/* Logo area — logo-on-white.svg above tab strip, centred (§3.1) */}
      <div
        style={{
          padding: '36px 28px 24px',
          borderBottom: `1px solid ${T.border}`,
          textAlign: 'center',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <img
            src={logoOnWhite}
            alt="Share Shack"
            style={{ width: 32, height: 32, display: 'block' }}
          />
          <span style={{ fontSize: 16, fontWeight: 600, color: T.text }}>Share Shack</span>
        </div>
      </div>

      {/* Tab strip (only for login/signup, not forgot) */}
      {!isForgot && (
        <div style={{ display: 'flex', borderBottom: `1px solid ${T.border}` }}>
          {(['login', 'signup'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => {
                setMode(tab);
                setErrors({});
              }}
              style={{
                flex: 1,
                padding: '11px 4px 13px',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                color: mode === tab ? T.accent : T.textMuted,
                fontSize: 13,
                fontFamily: 'inherit',
                fontWeight: mode === tab ? 700 : 400,
                borderBottom: `2px solid ${mode === tab ? T.accent : 'transparent'}`,
                marginBottom: -1,
                transition: 'all 0.15s',
              }}
            >
              {tab === 'login' ? 'Sign In' : 'Sign Up'}
            </button>
          ))}
        </div>
      )}

      {/* Form */}
      <div style={{ padding: '24px 28px 36px' }}>
        {isForgot && (
          <h2 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 600, color: T.text }}>
            Reset your password
          </h2>
        )}

        {/* Invite notice */}
        {inviteNotice && (
          <div
            style={{
              marginBottom: 16,
              padding: '10px 14px',
              borderRadius: RADIUS.sm,
              background: T.warningSoft,
              border: `1px solid ${T.warningBorder}`,
              color: T.warning,
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
            {inviteNotice}
            <div style={{ marginTop: 6 }}>
              <Link to="/" style={{ color: T.warning, fontSize: 12 }}>
                Return home and request a fresh invite
              </Link>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {mode === 'signup' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label htmlFor="displayName" style={{ fontSize: 14, fontWeight: 600, color: T.text }}>
                Display Name
              </label>
              <AuthInput
                id="displayName"
                type="text"
                placeholder="Your name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label htmlFor="email" style={{ fontSize: 14, fontWeight: 600, color: T.text }}>
              Email
            </label>
            <AuthInput
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setErrors((prev) => ({ ...prev, email: undefined }));
              }}
              error={errors.email}
            />
          </div>

          {!isForgot && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label htmlFor="password" style={{ fontSize: 14, fontWeight: 600, color: T.text }}>
                Password
              </label>
              <AuthInput
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setErrors((prev) => ({ ...prev, password: undefined }));
                }}
                error={errors.password}
              />
              {mode === 'signup' && !errors.password && (
                <p style={{ margin: 0, fontSize: 12, color: T.textMuted }}>
                  At least 6 characters. Common or breached passwords will be rejected.
                </p>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            style={{
              width: '100%',
              padding: '13px',
              borderRadius: RADIUS.md,
              background: T.accent,
              color: T.white,
              border: 'none',
              fontSize: 15,
              fontWeight: 600,
              cursor: isLoading ? 'default' : 'pointer',
              fontFamily: 'inherit',
              opacity: isLoading ? 0.7 : 1,
              marginTop: 4,
              boxShadow: SHADOW.btn.accent,
            }}
          >
            {isLoading
              ? 'Loading…'
              : isForgot
                ? 'Send Reset Link'
                : isLogin
                  ? 'Continue to Shack →'
                  : 'Sign Up'}
          </button>

          {!isLogin && !isForgot && (
            <p
              style={{
                fontSize: 12,
                color: T.textMuted,
                textAlign: 'center',
                marginTop: 12,
                lineHeight: 1.5,
              }}
            >
              By creating an account, you agree to our{' '}
              <Link to="/terms" style={{ color: T.accent, textDecoration: 'none' }}>
                Terms &amp; Conditions
              </Link>{' '}
              and{' '}
              <Link to="/privacy" style={{ color: T.accent, textDecoration: 'none' }}>
                Privacy Policy
              </Link>
              .
            </p>
          )}
        </form>

        {/* Footer links */}
        <div
          style={{
            marginTop: 20,
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          {isLogin && (
            <button
              type="button"
              onClick={() => {
                setMode('forgot');
                setErrors({});
              }}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: T.textMuted,
                fontSize: 12,
                fontFamily: 'inherit',
              }}
            >
              Forgot password?
            </button>
          )}
          <span style={{ fontSize: 12, color: T.textMuted }}>
            {isForgot
              ? 'Remember your password? '
              : isLogin
                ? "Don't have an account? "
                : 'Already have an account? '}
            <button
              type="button"
              onClick={() => {
                setMode(isForgot ? 'login' : isLogin ? 'signup' : 'login');
                setErrors({});
              }}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: T.accent,
                fontSize: 12,
                fontWeight: 600,
                fontFamily: 'inherit',
                padding: 0,
              }}
            >
              {isForgot ? 'Sign in' : isLogin ? 'Sign up' : 'Sign in'}
            </button>
          </span>
        </div>
      </div>
    </div>
  );

  // ── Wide layout (tablet / desktop) ────────────────────────────────────────
  if (isWide) {
    const leftFlex = bp === 'desktop' ? '55%' : '50%';
    const rightFlex = bp === 'desktop' ? '45%' : '50%';
    const colPad = bp === 'desktop' ? 64 : 48;

    return (
      <div style={{ minHeight: '100vh', display: 'flex' }}>
        {/* Left — marketing */}
        <div
          style={{
            flex: `0 0 ${leftFlex}`,
            background: `linear-gradient(160deg, ${T.surface} 0%, ${T.accentSoft} 100%)`,
            padding: `${colPad}px ${colPad}px`,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          {/* Wordmark */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 40 }}>
            <img src={shareShackIcon} alt="ShareShack" style={{ width: 40, height: 40 }} />
            <span
              style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em', color: T.text }}
            >
              <span style={{ color: T.accent }}>Share</span> Shack
            </span>
          </div>

          {/* Headline */}
          <h1
            style={{
              margin: '0 0 12px',
              fontSize: bp === 'desktop' ? 42 : 36,
              fontWeight: 700,
              color: T.text,
              letterSpacing: '-0.03em',
              lineHeight: 1.15,
            }}
          >
            Stop buying,
            <br />
            <span style={{ color: T.accent }}>Start sharing.</span>
          </h1>

          {/* Subheading */}
          <p style={{ margin: '0 0 48px', fontSize: 14, color: T.textSec, lineHeight: 1.65 }}>
            Private item sharing for people who actually trust each other.
          </p>

          {/* Feature cards */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <FeatureCard
              icon="📦"
              title="Track Every Loan"
              body="Know who has what, when it's due, and what's available — instantly."
            />
            <FeatureCard
              icon="🔒"
              title="Private Groups Only"
              body="Invite-only circles. No strangers, no marketplace, no drama."
            />
          </div>
        </div>

        {/* Right — auth form */}
        <div
          style={{
            flex: `0 0 ${rightFlex}`,
            background: T.bg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: `${colPad}px 40px`,
          }}
        >
          {authCard}
        </div>
      </div>
    );
  }

  // ── Mobile layout ─────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: T.bg }}>
      {/* Marketing strip (mobile) */}
      <div
        style={{
          background: `linear-gradient(160deg, ${T.surface} 0%, ${T.accentSoft} 100%)`,
          padding: '36px 20px 32px',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            marginBottom: 16,
          }}
        >
          <img src={shareShackIcon} alt="ShareShack" style={{ width: 36, height: 36 }} />
          <span style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em', color: T.text }}>
            <span style={{ color: T.accent }}>Share</span> Shack
          </span>
        </div>
        <h1
          style={{
            margin: '0 0 8px',
            fontSize: 28,
            fontWeight: 700,
            color: T.text,
            letterSpacing: '-0.025em',
            lineHeight: 1.2,
          }}
        >
          Stop buying,
          <br />
          <span style={{ color: T.accent }}>Start sharing.</span>
        </h1>
        <p style={{ margin: 0, fontSize: 13, color: T.textSec, lineHeight: 1.6 }}>
          Private item sharing for people who actually trust each other.
        </p>
      </div>

      {/* Auth card */}
      <div style={{ padding: '0 20px 40px', display: 'flex', justifyContent: 'center' }}>
        {authCard}
      </div>
    </div>
  );
}
